import { TestOrgNumberButton } from '../components/TestOrgNumberButton';
import { withTestOrgNumber } from '../utils/companyIdentifiers';
import { PingOnboardingPanel } from '../components/PingOnboardingPanel';
import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import { normalizePhoneNumber } from '../utils/phoneNumber';
import { PhoneInput } from '../components/PhoneInput';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DashboardLayout, Card, KycStatusPanel } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adyenOnboardingApi, companyApi, pingOnboardingApi, type AdyenAccount } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { isMockAdyenOnboardingUrl } from '../utils/adyenKyc';
import { useAdyenOnboardingPrompt } from '../hooks/useAdyenOnboardingPrompt';
import { kycIntroVariant, useKycIntro } from '../hooks/useKycIntro';
import { apiErrorText, saveErrorText } from '../utils/apiError';
import { notifyCompanyUpdated } from '../hooks/useCompanyProfileAttention';

interface CompanyFormData {
  organizationNumber: string;
  companyName: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  phone: string;
  vatNumber: string;
  email: string;
  website: string;
  experienceYears: string;
  specialties: string;
  languages: string;
  geographicArea: string;
  hourlyRate: string;
  responseTime: string;
  leadTime: string;
  companyPresentation: string;
}

type CompanySettingsTab = 'overview' | 'practical' | 'adyen' | 'payouts';

const companySettingsTabFromSearch = (search: string): CompanySettingsTab | null => {
  const tab = new URLSearchParams(search).get('tab');
  return tab === 'overview' || tab === 'practical' || tab === 'adyen' || tab === 'payouts' ? tab : null;
};

function companyToForm(company: any, user?: any): CompanyFormData {
  // First-time companies have these blank — fall back to the details of the
  // user who registered (only when the company value isn't already filled).
  return {
    organizationNumber: company.organizationNumber || '',
    companyName: company.companyName || '',
    address: company.address || '',
    postalCode: company.postalCode || '',
    city: company.city || '',
    country: company.country || 'Sverige',
    phone: company.phone || user?.phone || '',
    vatNumber: company.vatNumber || '',
    email: company.email || user?.email || '',
    website: company.website || '',
    experienceYears: String(company.experienceYears || ''),
    specialties: (company.specialties || []).join(', '),
    languages: (company.languages || []).join(', '),
    geographicArea: company.geographicArea || '',
    hourlyRate: company.hourlyRate || '',
    responseTime: company.responseTime || '',
    leadTime: company.leadTime || '',
    companyPresentation: company.companyPresentation || ''
  };
}

export function CompanySettings() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const showAdyenOnboardingPrompt = useAdyenOnboardingPrompt(user?.personaType === 'company' && !user?.isAdmin);
  const [company, setCompany] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTab, setActiveTab] = useState<CompanySettingsTab>(() => companySettingsTabFromSearch(location.search) ?? 'overview');
  const [pingOnboardingEnabled, setPingOnboardingEnabled] = useState<boolean | null>(null);
  // Adyen collects the payout account during KYC; old payout links go there too.
  const activeTab = selectedTab === 'payouts' && pingOnboardingEnabled === false ? 'adyen' : selectedTab;
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingAdyen, setIsLoadingAdyen] = useState(true);
  const [isStartingAdyen, setIsStartingAdyen] = useState(false);
  const [isRefreshingAdyen, setIsRefreshingAdyen] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [adyenAccount, setAdyenAccount] = useState<AdyenAccount | null>(null);
  const [adyenMessage, setAdyenMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formData, setFormData] = useState<CompanyFormData>(companyToForm({}));
  const hasAutoSyncedAdyen = useRef(false);

  useEffect(() => {
    loadCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    pingOnboardingApi.status()
      .then(status => { if (active) setPingOnboardingEnabled(status.enabled === true); })
      .catch(() => { if (active) setPingOnboardingEnabled(false); });
    return () => { active = false; };
  }, []);

  const loadCompany = async () => {
    try {
      const data = await companyApi.getCompany();
      setCompany(data);
      setFormData(companyToForm(data, user));
    } catch (error) {
      console.error('Failed to load company:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAdyenAccount = async () => {
    setIsLoadingAdyen(true);
    try {
      const account = await adyenOnboardingApi.getStatus();
      setAdyenAccount(account);
    } catch (error) {
      console.error('Failed to load Adyen onboarding status:', error);
      setAdyenMessage({ type: 'error', text: apiErrorText(t, error, 'companySettings.kycStatusLoadError') });
    } finally {
      setIsLoadingAdyen(false);
    }
  };

  useEffect(() => {
    loadAdyenAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tab = companySettingsTabFromSearch(location.search);
    if (tab) setActiveTab(tab);
  }, [location.search]);

  const kycIntro = useKycIntro();

  const handleStartAdyenOnboarding = async () => {
    setIsStartingAdyen(true);
    setAdyenMessage(null);
    try {
      const account = await adyenOnboardingApi.start();
      setAdyenAccount(account);
      if (isMockAdyenOnboardingUrl(account.onboardingUrl)) {
        setAdyenMessage({ type: 'success', text: t('companySettings.kycMockDone') });
      } else if (account.onboardingUrl) {
        setAdyenMessage({ type: 'success', text: t('companySettings.kycStarted') });
        window.open(account.onboardingUrl, '_blank', 'noopener,noreferrer');
      } else {
        setAdyenMessage({ type: 'success', text: t('companySettings.kycStarted') });
      }
    } catch (error) {
      setAdyenMessage({ type: 'error', text: apiErrorText(t, error, 'companySettings.kycStartError') });
    } finally {
      setIsStartingAdyen(false);
    }
  };

  const handleRefreshAdyenStatus = async (options?: { silent?: boolean }) => {
    setIsRefreshingAdyen(true);
    if (!options?.silent) setAdyenMessage(null);
    try {
      const account = await adyenOnboardingApi.refreshStatus();
      setAdyenAccount(account);
      if (!options?.silent) {
        setAdyenMessage({ type: 'success', text: t('companySettings.adyenStatusUpdated') });
      }
    } catch (error) {
      setAdyenMessage({ type: 'error', text: apiErrorText(t, error, 'companySettings.adyenStatusUpdateError') });
    } finally {
      setIsRefreshingAdyen(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'adyen' || hasAutoSyncedAdyen.current) return;
    hasAutoSyncedAdyen.current = true;
    void handleRefreshAdyenStatus({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleChange = (field: string, value: any) => {
    let next = value;
    if (field === 'vatNumber' && typeof value === 'string') next = value.replace(/\s/g, '').toUpperCase();
    if (field === 'organizationNumber' && typeof value === 'string') next = value.replace(/[^\d-]/g, '');
    setFormData(prev => ({ ...prev, [field]: next }));
  };

  // Swedish organisation number — Luhn / modulo-10 over the 10 digits.
  const isValidOrgNumber = (raw: string): boolean => {
    const digits = (raw || '').replace(/\D/g, '');
    if (digits.length !== 10) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      const product = Number(digits[i]) * (i % 2 === 0 ? 2 : 1);
      sum += product > 9 ? product - 9 : product;
    }
    sum += Number(digits[9]);
    return sum % 10 === 0;
  };

  // Swedish VAT: SE + 12 digits (org number + "01"), e.g. SE123456789001
  const isValidVatSE = (raw: string): boolean => /^SE\d{12}$/.test((raw || '').trim().toUpperCase());

  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Per-field live validation for the seven mandatory company fields.
  // Returns the visual state plus an explanatory message for the user.
  const fieldStatus = (field: string): { state: 'valid' | 'invalid'; message: string } | null => {
    const raw = (formData as any)[field];
    const value = typeof raw === 'string' ? raw.trim() : raw;
    switch (field) {
      case 'companyName':
        return value
          ? { state: 'valid', message: t('settings.companyDetails.fieldOk') }
          : { state: 'invalid', message: t('validation.required') };
      case 'organizationNumber':
        if (!value) return { state: 'invalid', message: t('validation.required') };
        return isValidOrgNumber(value)
          ? { state: 'valid', message: t('settings.companyDetails.orgNumberValid') }
          : { state: 'invalid', message: t('settings.companyDetails.orgNumberError') };
      case 'phone':
        if (!value) return { state: 'invalid', message: t('validation.required') };
        return normalizePhoneNumber(value)
          ? { state: 'valid', message: t('settings.companyDetails.phoneValid') }
          : { state: 'invalid', message: t('common.phone.invalid') };
      case 'vatNumber':
        if (!value) return { state: 'invalid', message: t('validation.required') };
        return isValidVatSE(value)
          ? { state: 'valid', message: t('settings.companyDetails.vatNumberValid') }
          : { state: 'invalid', message: t('settings.companyDetails.vatNumberError') };
      case 'email':
        if (!value) return { state: 'invalid', message: t('validation.required') };
        return emailRe.test(value)
          ? { state: 'valid', message: t('settings.companyDetails.emailValid') }
          : { state: 'invalid', message: t('validation.email') };
      default:
        return null;
    }
  };

  const requiredFields = ['companyName', 'organizationNumber', 'phone', 'vatNumber', 'email'];
  // Only the "Företag Översikt" tab is gated on its seven mandatory fields.
  const overviewValid = requiredFields.every(f => fieldStatus(f)?.state === 'valid');
  // "Praktisk information" can always be saved – its fields are not part of
  // requiredFields, so it must not be blocked by the overview validation.
  const saveDisabled = isSaving || (activeTab === 'overview' && !overviewValid);

  const fieldClass = (field: string): string => {
    if (!isEditing) return '';
    const s = fieldStatus(field);
    if (!s) return '';
    return s.state === 'valid' ? 'is-valid' : 'is-invalid';
  };

  const fieldFeedback = (field: string) => {
    if (!isEditing) return null;
    const s = fieldStatus(field);
    if (!s) return null;
    return (
      <div className={`${s.state === 'valid' ? 'valid-feedback' : 'invalid-feedback'} d-block`}>
        <i className={`feather-${s.state === 'valid' ? 'check-circle' : 'alert-circle'} me-1`}></i>
        {s.message}
      </div>
    );
  };

  const handleEdit = () => { setFormData(companyToForm(company || {}, user)); setIsEditing(true); setMessage(null); };
  const handleCancelEdit = () => { if (company) setFormData(companyToForm(company, user)); setIsEditing(false); };

  // Section header with the title on the left and the "Ändra" button top-right
  // (view mode only). flex-wrap keeps them from colliding on small screens.
  const sectionHeader = (title: string) => (
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
      <h5 className="fw-bold mb-0">{title}</h5>
      {!isEditing && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleEdit}
          aria-label={t('common.change', 'Ändra')}
        >
          <i className="feather-edit me-2"></i>{t('common.change', 'Ändra')}
        </button>
      )}
    </div>
  );

  const handleSave = async () => {
    if (activeTab === 'overview' && !overviewValid) {
      setMessage({ type: 'error', text: t('settings.companyDetails.fixErrorsBeforeSave') });
      return;
    }
    setIsSaving(true);
    try {
      await companyApi.updateCompany({
        ...formData,
        vatNumber: formData.vatNumber || null,
        website: formData.website || null,
        experienceYears: Number(formData.experienceYears) || 0,
        specialties: formData.specialties ? formData.specialties.split(',').map(s => s.trim()).filter(Boolean) : [],
        languages: formData.languages ? formData.languages.split(',').map(s => s.trim()).filter(Boolean) : [],
        companyPresentation: formData.companyPresentation || null
      } as any);
      notifyCompanyUpdated();
      setIsEditing(false);
      setMessage({ type: 'success', text: t('settings.companyDetails.saveSuccess') });
      // The saved details are read back in the background (ClickUp 869fgxq45).
      void companyApi.getCompany()
        .then(updated => {
          setCompany(updated);
          setFormData(companyToForm(updated, user));
        })
        .catch(error => console.error('Failed to reload company:', error));
    } catch (error) {
      setMessage({ type: 'error', text: saveErrorText(t, error, 'settings.companyDetails.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || pingOnboardingEnabled === null) {
    return (<DashboardLayout><div className="d-flex justify-content-center align-items-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t('common.loading')}</span></div></div></DashboardLayout>);
  }

  if (!company) {
    return (<DashboardLayout><div className="alert alert-danger">{t('common.error')}</div></DashboardLayout>);
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('settings.companyDetails.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('settings.companyDetails.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('settings.companyDetails.description')}</p>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-4`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-4 overflow-x-auto">
        <ul className="nav nav-tabs nav-tabs-custom-style flex-nowrap" role="tablist">
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
              <i className="feather-briefcase d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('settings.companyDetails.tabOverview', 'Företag Översikt')}</span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'practical' ? 'active' : ''}`} onClick={() => setActiveTab('practical')}>
              <i className="feather-info d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('marketplace.provider.practicalInfo', 'Praktisk information')}</span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'adyen' ? 'active' : ''}`} onClick={() => setActiveTab('adyen')}>
              <i className="feather-credit-card d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('companySettings.tabKyc')}</span>
            </button>
          </li>
          {pingOnboardingEnabled && <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'payouts' ? 'active' : ''}`} onClick={() => setActiveTab('payouts')}>
              {t('pingOnboarding.tab')}
            </button>
          </li>}
        </ul>
      </div>

      {/* === Företag Översikt Tab === */}
      {activeTab === 'overview' && (
      <Card>
        <div className="card-body">
          {sectionHeader(t('settings.companyDetails.tabOverview', 'Företag Översikt'))}
          {/* === Grunduppgifter === */}
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('settings.companyDetails.companyName')} *</label>
              <input type="text" className={`form-control ${fieldClass('companyName')}`} value={isEditing ? formData.companyName : (company.companyName || '')} disabled={!isEditing} onChange={e => handleChange('companyName', e.target.value)} />
              {fieldFeedback('companyName')}
              {(() => {
                // Mjuk varning: företagsnamnet är samma som firmatecknarens personnamn.
                // Då ser inbjudna kunder "inbjuden av X från X" – uppmana till korrigering.
                const companyNameValue = (isEditing ? formData.companyName : (company.companyName || '')).trim();
                const personName = (user?.fullName ?? '').trim();
                const sameName =
                  personName !== '' &&
                  companyNameValue !== '' &&
                  companyNameValue.toLowerCase() === personName.toLowerCase();
                return sameName ? (
                  <div className="form-text text-warning-emphasis mt-1">
                    <i className="feather-alert-triangle me-1"></i>
                    {t('settings.companyDetails.companyNameSameAsPersonWarning')}
                  </div>
                ) : null;
              })()}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('settings.companyDetails.organizationNumber')} *</label>
              <input type="text" className={`form-control ${fieldClass('organizationNumber')}`} value={isEditing ? formData.organizationNumber : (company.organizationNumber || '')} disabled={!isEditing || company.verifiedCompany === 'Verifierad'} onChange={e => handleChange('organizationNumber', e.target.value)} placeholder="XXXXXX-XXXX" />
              <TestOrgNumberButton disabled={!isEditing || company.verifiedCompany === 'Verifierad'} onGenerated={value => setFormData(previous => withTestOrgNumber(previous, value))} />
              {company.verifiedCompany === 'Verifierad' && <div className="form-text">{t('common.organizationIdentityLocked')}</div>}
              {fieldFeedback('organizationNumber')}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('settings.companyDetails.phone')} *</label>
              <PhoneInput name="phone" className={`form-control ${fieldClass('phone')}`} value={isEditing ? formData.phone : (company.phone || '')} disabled={!isEditing} onChange={e => handleChange('phone', e.target.value)} />
              {fieldFeedback('phone')}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('settings.companyDetails.vatNumber')} *</label>
              <input type="text" className={`form-control ${fieldClass('vatNumber')}`} value={isEditing ? formData.vatNumber : (company.vatNumber || '')} disabled={!isEditing} onChange={e => handleChange('vatNumber', e.target.value)} placeholder="SE123456789001" />
              {fieldFeedback('vatNumber')}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('settings.companyDetails.email')} *</label>
              <input type="email" className={`form-control ${fieldClass('email')}`} value={isEditing ? formData.email : (company.email || '')} disabled={!isEditing} onChange={e => handleChange('email', e.target.value)} />
              {fieldFeedback('email')}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('settings.companyDetails.website')}</label>
              <input type="text" className="form-control" value={isEditing ? formData.website : (company.website || '')} disabled={!isEditing} onChange={e => handleChange('website', e.target.value)} />
            </div>
          </div>

          {/* === Adress === */}
          <hr className="my-3" />
          <h6 className="fw-bold mb-3">{t('settings.companyDetails.address')}</h6>

          <div className="row">
            <div className="col-md-6 mb-3">
              <AddressAutocomplete
                name="address"
                label={t('settings.companyDetails.address')}
                onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                type="text"
                value={isEditing ? formData.address : (company.address || '')}
                disabled={!isEditing}
                onChange={e => handleChange('address', e.target.value)}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('settings.companyDetails.postalCode')}</label>
              <input type="text" className="form-control" value={isEditing ? formData.postalCode : (company.postalCode || '')} disabled={!isEditing} onChange={e => handleChange('postalCode', e.target.value)} />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('settings.companyDetails.city')}</label>
              <input type="text" className="form-control" value={isEditing ? formData.city : (company.city || '')} disabled={!isEditing} onChange={e => handleChange('city', e.target.value)} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('settings.companyDetails.country')}</label>
              <input type="text" className="form-control" value={isEditing ? formData.country : (company.country || '')} disabled={!isEditing} onChange={e => handleChange('country', e.target.value)} />
            </div>
          </div>

          {/* === Knappar (Spara/Avbryt endast i redigeringsläge) === */}
          {isEditing && (
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button className="btn btn-secondary" onClick={handleCancelEdit} disabled={isSaving}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saveDisabled}>
                {isSaving && <span className="spinner-border spinner-border-sm me-2"></span>}
                <i className="feather-save me-2"></i>{t('common.save')}
              </button>
            </div>
          )}
        </div>
      </Card>
      )}

      {activeTab === 'payouts' && pingOnboardingEnabled && <Card><div className="card-body"><PingOnboardingPanel /></div></Card>}

      {activeTab === 'adyen' && (
      <Card>
        <KycStatusPanel
          showOnboardingPrompts={showAdyenOnboardingPrompt}
          account={adyenAccount}
          loading={isLoadingAdyen}
          message={adyenMessage}
          showOpenAdyen
          showRefreshButton
          onRefresh={() => handleRefreshAdyenStatus()}
          isRefreshing={isRefreshingAdyen}
          showStartButton
          onStart={() => kycIntro.open(kycIntroVariant(adyenAccount, 'kyc'), () => void handleStartAdyenOnboarding())}
          isStarting={isStartingAdyen}
          technicalDetailsCollapsible
          plainLanguage
        />
      </Card>
      )}

      {/* === Praktisk information Tab === */}
      {activeTab === 'practical' && (
      <Card>
        <div className="card-body">
          {sectionHeader(t('marketplace.provider.practicalInfo', 'Praktisk information'))}
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('marketplace.provider.experience', 'Erfarenhetsår')}</label>
              <input type="number" className="form-control" value={isEditing ? formData.experienceYears : (company.experienceYears ?? 0)} disabled={!isEditing} onChange={e => handleChange('experienceYears', e.target.value)} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('marketplace.provider.geographicArea', 'Geografiskt område')}</label>
              <input type="text" className="form-control" value={isEditing ? formData.geographicArea : (company.geographicArea || '')} disabled={!isEditing} onChange={e => handleChange('geographicArea', e.target.value)} placeholder={t('companySettings.geographicAreaPlaceholder')} />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('marketplace.provider.specialties', 'Specialistområden')}</label>
              {isEditing ? (
                <>
                  <input type="text" className="form-control" value={formData.specialties} onChange={e => handleChange('specialties', e.target.value)} placeholder={t('companySettings.specialtiesPlaceholder')} />
                  <small className="text-muted">{t('common.commaSeparated', 'Kommaseparerade värden')}</small>
                </>
              ) : (
                <div>{(company.specialties || []).length > 0 ? (company.specialties || []).map((s: string, i: number) => <span key={i} className="badge bg-primary me-1 mb-1">{s}</span>) : <span className="text-muted">—</span>}</div>
              )}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('marketplace.provider.languages', 'Språk')}</label>
              {isEditing ? (
                <>
                  <input type="text" className="form-control" value={formData.languages} onChange={e => handleChange('languages', e.target.value)} placeholder={t('companySettings.languagesPlaceholder')} />
                  <small className="text-muted">{t('common.commaSeparated', 'Kommaseparerade värden')}</small>
                </>
              ) : (
                <div>{(company.languages || []).length > 0 ? (company.languages || []).map((l: string, i: number) => <span key={i} className="badge bg-info me-1 mb-1">{l}</span>) : <span className="text-muted">—</span>}</div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('marketplace.provider.hourlyRate', 'Timarvode')}</label>
              <input type="text" className="form-control" value={isEditing ? formData.hourlyRate : (company.hourlyRate || '')} disabled={!isEditing} onChange={e => handleChange('hourlyRate', e.target.value)} placeholder={t('companySettings.hourlyRatePlaceholder')} />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('marketplace.provider.responseTime', 'Svarstid')}</label>
              <input type="text" className="form-control" value={isEditing ? formData.responseTime : (company.responseTime || '')} disabled={!isEditing} onChange={e => handleChange('responseTime', e.target.value)} placeholder={t('companySettings.responseTimePlaceholder')} />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('marketplace.provider.leadTime', 'Ledtid')}</label>
              <input type="text" className="form-control" value={isEditing ? formData.leadTime : (company.leadTime || '')} disabled={!isEditing} onChange={e => handleChange('leadTime', e.target.value)} placeholder={t('companySettings.leadTimePlaceholder')} />
            </div>
          </div>

          {/* === Företagspresentation === */}
          <hr className="my-3" />
          <div className="mb-3">
            <label className="form-label">{t('settings.companyDetails.companyPresentation')}</label>
            {isEditing ? (
              <>
                <ReactQuill theme="snow" value={formData.companyPresentation} onChange={v => handleChange('companyPresentation', v)} style={{ height: '150px' }} />
                <div style={{ height: '45px' }}></div>
              </>
            ) : company.companyPresentation ? (
              <div className="form-control bg-light" style={{ minHeight: '60px', height: 'auto' }}>
                <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: company.companyPresentation }} />
              </div>
            ) : (
              <input type="text" className="form-control" value="" disabled />
            )}
          </div>

          {/* === Knappar (Spara/Avbryt endast i redigeringsläge) === */}
          {isEditing && (
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button className="btn btn-secondary" onClick={handleCancelEdit} disabled={isSaving}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saveDisabled}>
                {isSaving && <span className="spinner-border spinner-border-sm me-2"></span>}
                <i className="feather-save me-2"></i>{t('common.save')}
              </button>
            </div>
          )}

          {/* === Hur era kunder ser er information === */}
          <hr className="my-4" />
          <h6 className="fw-bold mb-3"><i className="feather-eye me-2"></i>{t('settings.companyDetails.customerPreview', 'Hur era beställare ser er information')}</h6>
          <p className="text-muted small mb-3">{t('settings.companyDetails.customerPreviewDescription', 'Nedan visas hur beställare ser ert företag på sin startsida.')}</p>

          <div className="card border">
            <div className="card-body">
              {/* Header */}
              <div className="d-flex align-items-start gap-3 mb-3">
                <div className="avatar-text rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 56, height: 56, fontSize: 18, fontWeight: 600 }}>
                  {(company.companyName || '').split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <span className="fw-bold fs-6">{company.companyName}</span>
                    <span className="badge bg-success"><i className="feather-check me-1" style={{ fontSize: 10 }}></i>{t('personHome.yourContractor', 'Din hantverkare')}</span>
                  </div>
                </div>
              </div>

              {/* Contact info */}
              {(company.phone || company.email || company.website) && (
                <div className="d-flex flex-wrap gap-3 mb-3 small">
                  {company.phone && (
                    <span className="text-muted"><i className="feather-phone me-1" style={{ fontSize: 13 }}></i>{company.phone}</span>
                  )}
                  {company.email && (
                    <span className="text-muted"><i className="feather-mail me-1" style={{ fontSize: 13 }}></i>{company.email}</span>
                  )}
                  {company.website && (
                    <span className="text-muted"><i className="feather-globe me-1" style={{ fontSize: 13 }}></i>{company.website}</span>
                  )}
                </div>
              )}

              {/* Specialties */}
              {(company.specialties || []).length > 0 && (
                <div className="mb-3">
                  <small className="fw-semibold text-muted d-block mb-1">{t('marketplace.provider.specialties', 'Specialistområden')}</small>
                  <div className="d-flex flex-wrap gap-1">
                    {(company.specialties || []).map((s: string, i: number) => (
                      <span key={i} className="badge bg-light text-dark border">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Practical info grid */}
              {((company.experienceYears || 0) > 0 || company.hourlyRate || company.geographicArea || company.responseTime || company.leadTime || (company.languages || []).length > 0) && (
                <div className="bg-light rounded p-3 mb-3">
                  <small className="fw-semibold text-muted d-block mb-2">{t('marketplace.provider.practicalInfo', 'Praktisk information')}</small>
                  <div className="row g-2">
                    {(company.experienceYears || 0) > 0 && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-award text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.experience', 'Erfarenhet')}</div>
                            <div className="fw-semibold small">{t('companySettings.experienceYearsValue', { count: company.experienceYears })}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {company.hourlyRate && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-tag text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.hourlyRate', 'Timarvode')}</div>
                            <div className="fw-semibold small">{company.hourlyRate}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {company.responseTime && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-clock text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.responseTime', 'Svarstid')}</div>
                            <div className="fw-semibold small">{company.responseTime}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {company.geographicArea && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-map-pin text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.geographicArea', 'Område')}</div>
                            <div className="fw-semibold small">{company.geographicArea}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {(company.languages || []).length > 0 && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-globe text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.languages', 'Språk')}</div>
                            <div className="fw-semibold small">{(company.languages || []).join(', ')}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {company.leadTime && (
                      <div className="col-6 col-md-4">
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-calendar text-muted" style={{ fontSize: 14 }}></i>
                          <div>
                            <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.leadTime', 'Ledtid')}</div>
                            <div className="fw-semibold small">{company.leadTime}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Company Presentation */}
              {company.companyPresentation ? (
                <div className="border rounded p-3 bg-white">
                  <small className="fw-semibold text-muted d-block mb-2">{t('settings.companyDetails.companyPresentation', 'Företagspresentation')}</small>
                  <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: company.companyPresentation }} />
                </div>
              ) : (
                <p className="text-muted mb-0">{t('personHome.noPresentation', 'Ingen presentation tillagd ännu.')}</p>
              )}
            </div>
          </div>
        </div>
      </Card>
      )}
      {kycIntro.modal}
    </DashboardLayout>
  );
}
